import { createAsyncThunk, createSlice, createSelector } from "@reduxjs/toolkit";
import { EBaseUrl } from "index";
import thunkOptions from "store/config/thunkOptions";
import {
  ICampaignDetailsState,
  ITrigger,
  ITemplate,
  ICampaignDetailed,
  ICampaignSurvey,
  IDistributionSchedule,
  ICampaignSurveyDetails,
  ICreateCampaignSurveyResponse,
  IAddEditSurveyTemplateRequest,
  IUpdateSurveyRequest,
  ICreateSurveyLogic,
  ISurveyLogicResponse,
  IUpdateSurveyTemplateRequest,
  ISurveysStatisticsGridResponse,
} from "store/interfaces/campaignDetails";
import { IGridRequest, IPaginated, IState } from "store/interfaces/main";
import { IUpdateUserProfileImage } from "store/interfaces/users";
import { api } from "store/services/apiService";

const name = "CAMPAIGN_DETAILS";

const initialState: ICampaignDetailsState = {
  triggers: [],
  templates: null,
  surveys: [],
  details: null,
  selectedSurvey: 0,
  selectedTemplateID: 0,
  surveyTemplateID: 0,
  surveyAppearance: null,
  surveyLogic: null,
  surveyDetails: null,
  surveyStatisticsData: null,
  pinnedSurveys: [],
  form: {
    survey: null,
    settings: null,
  },
};

export const GetCampaignSurveyById = createAsyncThunk<
  ICampaignSurveyDetails,
  { id: number; language_id: number }
>(
  `${name}/GetCampaignSurveyById`,
  async ({ id, language_id }) => {
    return (await api.get(`${EBaseUrl.API}/Survey/${id}/${language_id}`)).data;
  },
  thunkOptions
);

export const GetCampaignSurveyByDefaultId = createAsyncThunk<
  ICampaignSurveyDetails,
  { id: number; language_id: number }
>(
  `${name}/GetCampaignSurveyByDefaultId`,
  async ({ id, language_id }) => {
    return (await api.get(`${EBaseUrl.API}/Survey/${id}/${language_id}`)).data;
  },
  thunkOptions
);

export const ChangeCampaignSurveyPositions = createAsyncThunk<
  unknown,
  {
    data: { surveyPositions: ICampaignSurvey[]; languageID: number };
    id: number;
  }
>(
  `${name}/ChangeCampaignSurveyPositions`,
  async ({ data, id }) => {
    return (await api.put(`${EBaseUrl.API}/Survey/Status/${id}`, data)).data;
  },
  thunkOptions
);

export const CreateSurvey = createAsyncThunk<
  ICreateCampaignSurveyResponse,
  IUpdateSurveyRequest
>(
  `${name}/CreateSurvey`,
  async (formData) => {
    return (await api.post(`${EBaseUrl.API}/Survey`, formData)).data;
  },
  thunkOptions
);

export const RemoveCampaignSurvey = createAsyncThunk<
  unknown,
  { id: number; language_id: number }
>(
  `${name}/RemoveCampaignSurvey`,
  async ({ id, language_id }) => {
    return (await api.delete(`${EBaseUrl.API}/Survey/${id}/${language_id}`))
      .data;
  },
  thunkOptions
);

export const GetCampaignSurveyTemplateById = createAsyncThunk<
  ITemplate,
  number
>(
  `${name}/GetCampaignSurveyTemplateById`,
  async (id: number) => {
    return (await api.get(`${EBaseUrl.API}/SurveyTemplate/${id}`)).data;
  },
  thunkOptions
);

export const GetCampaignTriggers = createAsyncThunk<ITrigger[]>(
  `${name}/GetCampaignTriggers`,
  async () => {
    return (await api.get(`${EBaseUrl.API}/Triggers`)).data;
  }
);

export const GetCampaignById = createAsyncThunk<ICampaignDetailed, number>(
  `${name}/GetCampaignById`,
  async (id: number) => {
    return (await api.get(`${EBaseUrl.API}/Campaign/${id}`)).data;
  },
  thunkOptions
);

export const GetTemplates = createAsyncThunk<
  IPaginated<ITemplate>,
  { id: number; formData: IGridRequest }
>(
  `${name}/GetTemplates`,
  async (body) => {
    return (
      await api.post(`${EBaseUrl.API}/Templates/${body?.id}`, body?.formData)
    ).data;
  },
  thunkOptions
);

export const GetSurveys = createAsyncThunk<ICampaignSurvey[], number>(
  `${name}/GetSurveys`,
  async (id: number) => {
    return (await api.get(`${EBaseUrl.API}/Surveys/${id}`)).data;
  },
  thunkOptions
);

export const GetSurveysInitial = createAsyncThunk<ICampaignSurvey[], any>(
  `${name}/GetSurveysInitial`,
  async ({ id, language_id }) => {
    return (await api.get(`${EBaseUrl.API}/Surveys/${id}/${language_id}`)).data;
  },
  thunkOptions
);

export const UpdateSurvey = createAsyncThunk<
  unknown,
  { data: IUpdateSurveyRequest; id: number }
>(
  `${name}/UpdateSurvey`,
  async ({ id, data }: { id: number; data: IUpdateSurveyRequest }) => {
    return (await api.put(`${EBaseUrl.API}/Survey/${id}`, data)).data;
  },
  thunkOptions
);

export const CreateSurveyTemplate = createAsyncThunk<
  unknown,
  IAddEditSurveyTemplateRequest
>(
  `${name}/CreateSurveyTemplate`,
  async (data: IAddEditSurveyTemplateRequest) => {
    return (await api.post(`${EBaseUrl.API}/SurveyTemplate/Public`, data)).data;
  },
  thunkOptions
);

export const UpdateSurveyTemplate = createAsyncThunk<
  unknown,
  { data: IAddEditSurveyTemplateRequest; id: number }
>(
  `${name}/UpdateSurveyTemplate`,
  async ({ id, data }: { id: number; data: IAddEditSurveyTemplateRequest }) => {
    return (await api.put(`${EBaseUrl.API}/SurveyTemplate/Custom/${id}`, data))
      .data;
  },
  thunkOptions
);

export const UpdateSurveyTemplateDefault = createAsyncThunk<
  unknown,
  { data: IUpdateSurveyTemplateRequest; id: number }
>(
  `${name}/UpdateSurveyTemplateDefault`,
  async ({ id, data }: { id: number; data: IUpdateSurveyTemplateRequest }) => {
    return (await api.put(`${EBaseUrl.API}/SurveyTemplate/Default/${id}`, data))
      .data;
  },
  thunkOptions
);

export const DeleteCampaignTemplate = createAsyncThunk<unknown, number>(
  `${name}/RemoveCampaignTemplate`,
  async (id) => {
    return (await api.delete(`${EBaseUrl.API}/SurveyTemplate/Campaign/${id}`))
      .data;
  },
  thunkOptions
);

export const DeleteSurveyTemplate = createAsyncThunk<unknown, number>(
  `${name}/RemoveSurveyTemplate`,
  async (id) => {
    return (await api.delete(`${EBaseUrl.API}/SurveyTemplate/${id}`)).data;
  },
  thunkOptions
);

export const DeleteCustomTemplate = createAsyncThunk<unknown, number>(
  `${name}/DeleteTemplate`,
  async (id) => {
    return (await api.delete(`${EBaseUrl.API}/SurveyTemplate/Public/${id}`))
      .data;
  },
  thunkOptions
);

export const ApplySurvey = createAsyncThunk<
  unknown,
  { surveyID: string; templateID: number }
>(
  `${name}/ApplySurvey`,
  async (formData) => {
    return (await api.post(`${EBaseUrl.API}/SurveyTemplate/Apply`, formData))
      .data;
  },
  thunkOptions
);

export const ApplyForAllSurveys = createAsyncThunk<
  unknown,
  { campaignID: string; templateID: number }
>(
  `${name}/ApplyForAllSurveys`,
  async (formData) => {
    return (await api.post(`${EBaseUrl.API}/SurveyTemplate/ApplyAll`, formData))
      .data;
  },
  thunkOptions
);

export const GetSurveyLogic = createAsyncThunk<ISurveyLogicResponse, any>(
  `${name}/GetSurveyLogic`,
  async ({ id, languageID }) => {
    return (await api.get(`${EBaseUrl.API}/SurveyLogic/${id}/${languageID}`))
      .data;
  },
  thunkOptions
);

export const CreateSurveyLogic = createAsyncThunk<
  unknown,
  { formData: ICreateSurveyLogic; id: number }
>(
  `${name}/CreateSurveyLogic`,
  async ({ formData, id }) => {
    return (await api.post(`${EBaseUrl.API}/SurveyLogic?id=${id}`, formData))
      .data;
  },
  thunkOptions
);

export const CreateSurveyLogicForComment = createAsyncThunk<
  unknown,
  { nextID: number; id: number; languageID: number }
>(
  `${name}/CreateSurveyLogicForComment`,
  async (formData) => {
    return (
      await api.post(
        `${EBaseUrl.API}/SurveyLogic/AnyCase?id=${formData.id}`,
        formData
      )
    ).data;
  },
  thunkOptions
);

export const DeleteSurveyLogic = createAsyncThunk<
  unknown,
  { iDs: number[]; languageID: number }
>(
  `${name}/DeleteSurveyLogic`,
  async (data) => {
    return (await api.delete(`${EBaseUrl.API}/SurveyLogic`, { data })).data;
  },
  thunkOptions
);

export const DeleteSurveyLogicBySurveyId = createAsyncThunk<
  unknown,
  { id: number; languageID: number }
>(
  `${name}/DeleteSurveyLogicBySurveyId`,
  async ({ id, languageID }) => {
    return (await api.delete(`${EBaseUrl.API}/SurveyLogic/${id}/${languageID}`))
      .data;
  },
  thunkOptions
);

export const DistributionSchedule = createAsyncThunk<
  unknown,
  { data: IDistributionSchedule; id: number }
>(
  `${name}/DistributionSchedule`,
  async ({ id, data }: { id: number; data: IDistributionSchedule }) => {
    return (await api.put(`${EBaseUrl.API}/Campaign/Schedule/New/${id}`, data))
      .data;
  },
  thunkOptions
);

export const EmailDistributionImageUpload = createAsyncThunk<
  unknown,
  IUpdateUserProfileImage
>(
  `${name}/DistributionSchedule`,
  async (body: IUpdateUserProfileImage) => {
    return (await api.post(`${EBaseUrl.API}/Campaign/ImageUpload`, body)).data;
  },
  thunkOptions
);

export const OldDistributionSchedule = createAsyncThunk<
  unknown,
  { data: IDistributionSchedule; id: number }
>(
  `${name}/DistributionSchedule`,
  async ({ id, data }: { id: number; data: IDistributionSchedule }) => {
    return (await api.put(`${EBaseUrl.API}/Campaign/Schedule/${id}`, data))
      .data;
  },
  thunkOptions
);

export const GetSurveyStatisticsGrid = createAsyncThunk<
  IPaginated<ISurveysStatisticsGridResponse>,
  number
>(
  `${name}/GetSurveyStatisticsGrid`,
  async (campaignID) => {
    return (
      await api.get(
        `${EBaseUrl.API}/Survey/SurveysRespondsGrid?campaignID=${campaignID}`
      )
    ).data;
  },
  thunkOptions
);

export const PinAllSurveys = createAsyncThunk<unknown, number>(
  `${name}/PinAllSurveys`,
  async (campaignId) => {
    return (
      await api.post(`${EBaseUrl.API}/Survey/PinAll?campaignId=${campaignId}`)
    ).data;
  },
  thunkOptions
);

export const GetAllTemplatesList = createAsyncThunk<ITemplate[], number>(
  `${name}/GetAllTemplatesList`,
  async (id) => {
    return (await api.get(`${EBaseUrl.API}/AllTemplates/${id}`)).data;
  },
  thunkOptions
);

export const GetAllPinnedSurveys = createAsyncThunk<
  ISurveysStatisticsGridResponse[],
  number | string
>(
  `${name}/GetAllPinnedSurveys`,
  async (campaignID?: number | string) => {
    return (
      await api.get(
        `${EBaseUrl.API}/Survey/PinnedSurveys?campaignID=${campaignID}`
      )
    ).data;
  },
  thunkOptions
);

export const PinSurvey = createAsyncThunk<
  unknown,
  {
    campaignID: number;
    surveyID: number;
  }
>(
  `${name}/PinSurvey`,
  async (formData) => {
    return (await api.post(`${EBaseUrl.API}/Survey/Pin`, formData)).data;
  },
  thunkOptions
);

export const UnPinAllSurveys = createAsyncThunk<unknown, number>(
  `${name}/UnPinAllSurveys`,
  async (campaignID) => {
    return (
      await api.delete(
        `${EBaseUrl.API}/Survey/UnPinAll?campaignID=${campaignID}`
      )
    ).data;
  },
  thunkOptions
);

export const UnPinSurvey = createAsyncThunk<
  unknown,
  {
    campaignID: number;
    surveyID: number;
  }
>(
  `${name}/UnPinSurvey`,
  async ({ surveyID, campaignID }) => {
    return (
      await api.delete(
        `${EBaseUrl.API}/Survey/UnPin?campaignID=${campaignID}&surveyId=${surveyID}`
      )
    ).data;
  },
  thunkOptions
);

export const ExportSurveyResponses = createAsyncThunk<string, number>(
  `${name}/ExportStatistics`,
  async (campaignId?: number) => {
    return await api.get(
      `${EBaseUrl.API}/Survey/ExportSurveysRespondsGridToExcel?campaignId=${campaignId}`,
      {
        responseType: "blob",
      }
    );
  }
);

const campaignDetailSlice = createSlice({
  initialState,
  name,
  reducers: {
    setSelectedSurvey(state, { payload }) {
      state.selectedSurvey = payload;
    },
    setSurveyForm(state, { payload }) {
      state.form.survey = payload;
    },
    setSettingsForm(state, { payload }) {
      state.form.settings = payload;
    },
    setSelectedTemplateID(state, { payload }) {
      state.selectedTemplateID = payload;
    },
    resetCampaignDetails() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(GetCampaignTriggers.fulfilled, (state, { payload }) => {
      state.triggers = payload;
    });
    builder.addCase(GetTemplates.fulfilled, (state, { payload }) => {
      state.templates = payload;
    });
    builder.addCase(GetCampaignById.fulfilled, (state, { payload }) => {
      state.details = payload;
    });
    builder.addCase(GetSurveyStatisticsGrid.fulfilled, (state, { payload }) => {
      state.surveyStatisticsData = payload;
    });
    builder.addCase(GetSurveys.fulfilled, (state, { payload }) => {
      state.surveys = payload;
    });
    builder.addCase(GetSurveysInitial.fulfilled, (state, { payload }) => {
      state.surveys = payload;
      // Preserve the currently selected survey if it still exists in the new payload
      // Only reset to first question if the current selection doesn't exist
      const currentSurveyExists = payload.some(
        (survey) => survey.id === state.selectedSurvey
      );
      if (!currentSurveyExists && payload.length > 0) {
        state.selectedSurvey = payload[0].id;
      }
    });
    builder.addCase(GetCampaignSurveyById.fulfilled, (state, { payload }) => {
      state.surveyDetails = payload;
    });
    builder.addCase(GetSurveyLogic.fulfilled, (state, { payload }) => {
      state.surveyLogic = payload;
    });
    builder.addCase(GetAllPinnedSurveys.fulfilled, (state, { payload }) => {
      state.pinnedSurveys = payload;
    });
    builder.addCase(
      GetCampaignSurveyTemplateById.fulfilled,
      (state, { payload }) => {
        state.surveyTemplateID = payload.id;
        state.surveyAppearance = payload;
      }
    );
  },
});

export const selectSelectedSurvey = (state: IState) =>
  state.campaignDetails.selectedSurvey;
export const selectTriggers = (state: IState) => state.campaignDetails.triggers;
export const selectSelectedTemplateID = (state: IState) =>
  state.campaignDetails.selectedTemplateID;
export const selectSurveyTemplateID = (state: IState) =>
  state.campaignDetails.surveyTemplateID;
export const selectSurveyAppearance = (state: IState) =>
  state.campaignDetails.surveyAppearance;
export const selectCampaignInfo = (state: IState) =>
  state.campaignDetails.details;
export const selectCampaignSurveys = (state: IState) =>
  state.campaignDetails.surveys;
export const selectTemplates = (state: IState) =>
  state.campaignDetails.templates;
export const selectForm = (state: IState) => state.campaignDetails.form;
export const selectSurveyLogic = (state: IState) =>
  state.campaignDetails.surveyLogic;
export const selectStatisticsData = (state: IState) =>
  state.campaignDetails.surveyStatisticsData;
export const selectPinnedSurveys = (state: IState) =>
  state.campaignDetails.pinnedSurveys;

// Memoized selector to prevent unnecessary rerenders
// Only returns a new object when the underlying state actually changes
export const selectSurveyInfo = createSelector(
  [
    (state: IState) => state.campaignDetails.surveyDetails,
    (state: IState) => state.campaignDetails.selectedTemplateID,
    (state: IState) => state.campaignDetails.templates?.displayData,
    (state: IState) => state.campaignDetails.surveyAppearance,
  ],
  (surveyDetails, selectedTemplateID, templatesDisplayData, surveyAppearance) => {
    return {
      details: surveyDetails,
      template: selectedTemplateID
        ? templatesDisplayData?.find((i) => i.id === selectedTemplateID)
        : surveyAppearance,
    };
  }
);

export const {
  setSelectedSurvey,
  setSettingsForm,
  setSurveyForm,
  resetCampaignDetails,
  setSelectedTemplateID,
} = campaignDetailSlice.actions;
export default campaignDetailSlice.reducer;
